import { MiscUtil } from './MiscUtil';
import { NonApiCommand, NonApiCommandName } from './NonApiCommand';
import { TsConfig } from './VizclientUtils';

type GetScaleCallback = (scaleFactor: number, scrollX: number, scrollY: number) => void;

class ZoomScaleUtil {
  private static PendingCallbacks: { [key: string]: GetScaleCallback } = {};
  private static outerMessageSource: Window;
  private static nextId: number = 1;
  private static lastScaleFactor: number = 1;

  public static initialize(): void {
    window.addEventListener('message', (evt) => ZoomScaleUtil.OnMessage(window, evt), false);
  }

  /**
  * Returns the zoom scale factor for the local window.
  * This will be a value ranging from (0, inf), where a value larger than 1 means you are zoomed in.
  * Note that the scale factor may be slightly inaccurate for desktop browsers, so use at your own peril.
  */
  private static get ZoomScaleForLocalWindow(): number {
    // TFSID: 567082 - Use the document's clientWidth instead of screen.width for
    // the unzoomed width, because when we are in an iframe embedded in another
    // page we want our unzoomed width to reflect the size of the iframe.
    let width: number = document.documentElement.clientWidth;
    let zoomedWidth: number = window.innerWidth;
    if (!TsConfig.IsMobile()) {
      zoomedWidth -= 17;
    }
    return width / zoomedWidth;
  }

  private static get IsScaleFactorCommunicationAvailable(): boolean {
    return !MiscUtil.IsNullOrUndefined(ZoomScaleUtil.outerMessageSource);
  }

  private static OnMessage(target: Window, e: Event): void {
    let messageEvent: MessageEvent = e as MessageEvent;
    // TFS 389713: Make sure our type is correct before parsing
    if (typeof messageEvent.data !== 'string') {
      return;
    }

    let command: NonApiCommand = NonApiCommand.Parse(messageEvent.data.toString());
    if (command.Name === NonApiCommandName.ScaleFactorResponse) {
      let id: string = command.Parameters[0];

      // Save the scale factor so that it can be retrieved synchronously later
      ZoomScaleUtil.lastScaleFactor = Number(command.Parameters[1]);
      let scrollX: number = Number(command.Parameters[2]);
      let scrollY: number = Number(command.Parameters[3]);
      let cb: GetScaleCallback = ZoomScaleUtil.PendingCallbacks[id];
      delete ZoomScaleUtil.PendingCallbacks.id;
      ZoomScaleUtil.outerMessageSource = messageEvent.source as Window;
      if (!MiscUtil.IsNullOrUndefined(cb)) {
        cb(ZoomScaleUtil.lastScaleFactor, scrollX, scrollY);
      }
    }
  }

  private static RequestScaleFactor(callback: GetScaleCallback = null): void {
    let id: string = (++ZoomScaleUtil.nextId).toString();
    ZoomScaleUtil.PendingCallbacks[id] = callback;
    let command = new NonApiCommand(NonApiCommandName.ScaleFactorRequest, [id]);
    ZoomScaleUtil.outerMessageSource.postMessage(command.Serialize(), '*');
  }

  /**
  * Invokes the callback with the current browser viewport scale factor.
  * Consumers that cannot handle asynchronous behavior should use <see cref="GetScaleSync"/> instead.
  */
  public static GetScaleAsync(callback: GetScaleCallback): void {
    if (ZoomScaleUtil.IsScaleFactorCommunicationAvailable) {
      ZoomScaleUtil.RequestScaleFactor(callback);
    } else if (!MiscUtil.IsNullOrUndefined(callback)) {
      callback(ZoomScaleUtil.ZoomScaleForLocalWindow, 0, 0);
    }
  }

  /**
  * Returns the last known browser viewport scale factor. Note that this scale factor may be stale when we are embedded.
  * Consumers that require the zoom scale to always be accurate and can tolerate asynchronous behavior should use
  * <see cref="GetScaleAsync"/> instead.
  */
  public static GetScaleSync(): number {
    if (ZoomScaleUtil.IsScaleFactorCommunicationAvailable) {
      ZoomScaleUtil.RequestScaleFactor();
      return ZoomScaleUtil.lastScaleFactor;
    } else {
      return ZoomScaleUtil.ZoomScaleForLocalWindow;
    }
  }
}

ZoomScaleUtil.initialize();

export { GetScaleCallback, ZoomScaleUtil };
